/**
 * Tips Retention HTTP Client for Personnel Domain (V3)
 *
 * Implements ITipsRetentionApiPort using:
 * 1. ParrotAPI - Store-level retention (reuses V2 endpoint)
 * 2. Personnel API V2 - Role-specific retentions (new endpoint)
 *
 * @module domains/personnel/infrastructure/api/TipsRetentionHttpClient
 */

import type { AxiosInstance } from 'axios';
import type { ITipsRetentionApiPort } from '@personnel/application/ports/ITipsRetentionApiPort';
import type { UpdateTipsRetentionParamsV3, RoleRetentionItem } from '@personnel/application/dtos/TipsRetentionDto';

/**
 * Request body for updating role configurations
 */
export type UpdateRoleConfigurationsRequest = RoleRetentionItem[];

/**
 * Response from ParrotAPI tips retention update
 */
export interface UpdateTipsRetentionParrotResponse {
  data: {
    tipRetentionType: string;
    tipRetentionAmount: number;
  };
  datetime: string;
}

/**
 * Response from Personnel API role configurations update
 */
export interface UpdateRoleConfigurationsResponse {
  data: Array<{
    roleUuid: string;
    tipRetentionAmount: string;
  }>;
  datetime: string;
}

/**
 * Creates the tips retention API port implementation.
 *
 * Handles the two-step process:
 * 1. Update store retention in ParrotAPI (v1)
 * 2. Update role configurations in Personnel API (v2)
 *
 * @param props.httpClient - Axios instance
 * @returns ITipsRetentionApiPort implementation
 */
export const createTipsRetentionHttpClient = (props: { httpClient: AxiosInstance }): ITipsRetentionApiPort => {
  const { httpClient } = props;

  /**
   * Step 1: Update store-level tips retention in ParrotAPI
   * PATCH /api/v1/stores/{storeUuid}/retention
   */
  const updateStoreRetention = async (
    storeUuid: string,
    tipRetentionType: string,
    tipRetentionAmount: number,
  ): Promise<UpdateTipsRetentionParrotResponse> => {
    const response = await httpClient.patch<UpdateTipsRetentionParrotResponse>(`/v1/stores/${storeUuid}/retention`, {
      tipRetentionType,
      tipRetentionAmount,
    });

    return response.data;
  };

  /**
   * Step 2: Update role-specific retentions in Personnel API
   * PATCH /api/v2/role-configurations
   */
  const updateRoleConfigurations = async (
    roleRetentions: UpdateRoleConfigurationsRequest,
  ): Promise<UpdateRoleConfigurationsResponse> => {
    const response = await httpClient.patch<UpdateRoleConfigurationsResponse>(
      `/v2/role-configurations`,
      roleRetentions,
    );

    return response.data;
  };

  const updateTipsRetention = async (params: UpdateTipsRetentionParamsV3): Promise<void> => {
    // Step 1: Update ParrotAPI (store-level retention)
    await updateStoreRetention(params.storeUuid, params.tipRetentionType, params.tipRetentionAmount);

    // Step 2: Update Personnel API (role-level retentions)
    await updateRoleConfigurations(params.roleRetentions);
  };

  return { updateTipsRetention };
};
